import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Flex,
    FormErrorMessage,
    FormControl,
    FormLabel,
    Input,
    InputRightElement,
    IconButton,
    Text,
    InputGroup,
    Divider,
    Stack
} from "@chakra-ui/react";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { userService } from "services/userService";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import { checkErrors, hasError, validatePassword } from "utils/forms-utils";

function ChangePasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const [isLoading, setIsLoading] = useState(false);

    const ids = useMemo(() => ({
        password: "password",
        newPassword: "newPassword",
        confirmPassword: "confirmPassword"
    }), []);

    const [showPasswords, setShowPasswords] = useState({
        [ids.password]: false,
        [ids.newPassword]: false,
        [ids.confirmPassword]: false
    });

    const initFormData = () => ({
        [ids.password]: {
            value: "",
            validations: [{ required: true }],
        },
        [ids.newPassword]: {
            value: "",
            validations: [
                { required: true },
                { custom: validatePassword(t) }
            ],
        },
        [ids.confirmPassword]: {
            value: "",
            validations: [{ required: true }],
        }
    });

    const [formData, setFormData] = useState(initFormData());
    const [inputError, setInputError] = useState({});

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const togglePasswordVisibility = (fieldId) => {
        setShowPasswords((prevState) => ({
            ...prevState,
            [fieldId]: !prevState[fieldId]
        }));
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        const newFormData = { ...formData, [id]: { ...formData[id], value: value } };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    const handlePasswordChange = (e) => {
        const { id, value } = e.target;

        const nextFormData = {
            ...formData,
            [id]: { ...formData[id], value: value }
        };

        const baseErrors = checkErrors({
            formData: nextFormData,
            inputError,
            name: id
        });

        const customErrors = validatePassword({
            newPassword: nextFormData[ids.newPassword].value,
            confirmPassword: nextFormData[ids.confirmPassword].value
        });

        const nextInputErrors = {
            ...baseErrors,
            ...customErrors
        };

        setFormData(nextFormData);
        setInputError(nextInputErrors);
    };

    const handleClear = () => {
        setFormData(initFormData());
        setInputError({});
        navigate(-1);
    };

    const toDTO = () => {
        return {
            senha_atual: formData[ids.password].value,
            nova_senha: formData[ids.newPassword].value
        };
    };

    const handleSubmit = async () => {
        if (isFormInvalid) return;

        try {
            setIsLoading(true);
            const data = toDTO();
            const responseNewPassword = await userService.changePassword(data);

            if (!responseNewPassword) {
                showToast({ title: t("system.messages.errors.unexpected"), status: "error" });
                return;
            }
            showToast({ title: t("settings.user.change-password.messages.success"), status: "success" });
            handleClear();
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            return handleSubmit();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            return handleClear();
        }
    };

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
            <Card>
                <CardBody px="22px">
                    <form>
                        <Text fontSize="lg" fontWeight="bold">{t("settings.user.change-password.title")}</Text>
                        <Divider m="1em" />

                        <Stack spacing="20px">
                            <FormControl isRequired isInvalid={inputError[ids.password]}>
                                <FormLabel fontSize="sm">{t('settings.user.change-password.label.current-password')}</FormLabel>
                                <InputGroup>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        type={showPasswords[ids.password] ? "text" : "password"}
                                        id={ids.password}
                                        value={formData[ids.password].value}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('settings.user.change-password.placeholder.current-password')}
                                        maxLength={255}
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            aria-label="Show password"
                                            icon={showPasswords[ids.password] ? <HiEyeOff /> : <HiEye />}
                                            onClick={() => togglePasswordVisibility(ids.password)}
                                            variant="link"
                                            size="sm"
                                        />
                                    </InputRightElement>
                                </InputGroup>
                                <FormErrorMessage>
                                    {t(inputError[ids.password])}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl isRequired isInvalid={inputError[ids.newPassword]}>
                                <FormLabel fontSize="sm">{t('settings.user.change-password.label.new-password')}</FormLabel>
                                <InputGroup>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        type={showPasswords[ids.newPassword] ? "text" : "password"}
                                        id={ids.newPassword}
                                        value={formData[ids.newPassword].value}
                                        onChange={handlePasswordChange}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('settings.user.change-password.placeholder.new-password')}
                                        maxLength={255}
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            aria-label="Show password"
                                            icon={showPasswords[ids.newPassword] ? <HiEyeOff /> : <HiEye />}
                                            onClick={() => togglePasswordVisibility(ids.newPassword)}
                                            variant="link"
                                            size="sm"
                                        />
                                    </InputRightElement>
                                </InputGroup>
                                <FormErrorMessage>
                                    {t(inputError[ids.newPassword])}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl isRequired isInvalid={inputError[ids.confirmPassword]}>
                                <FormLabel fontSize="sm">{t('settings.user.change-password.label.confirm-password')}</FormLabel>
                                <InputGroup>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        type={showPasswords[ids.confirmPassword] ? "text" : "password"}
                                        id={ids.confirmPassword}
                                        value={formData[ids.confirmPassword].value}
                                        onChange={handlePasswordChange}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('settings.user.change-password.placeholder.confirm-password')}
                                        maxLength={255}
                                    />
                                    <InputRightElement>
                                        <IconButton
                                            aria-label="Show password"
                                            icon={showPasswords[ids.confirmPassword] ? <HiEyeOff /> : <HiEye />}
                                            onClick={() => togglePasswordVisibility(ids.confirmPassword)}
                                            variant="link"
                                            size="sm"
                                        />
                                    </InputRightElement>
                                </InputGroup>
                                <FormErrorMessage>
                                    {t(inputError[ids.confirmPassword])}
                                </FormErrorMessage>
                            </FormControl>
                        </Stack>

                        <Flex justifyContent="space-between" mt="2em">
                            <Button
                                w="125px"
                                variant="no-effects"
                                bg="gray.100"
                                color="gray.700"
                                fontSize="sm"
                                fontWeight="bold"
                                onClick={handleClear}>
                                <Text>
                                    {t("system.actions.cancel")}
                                </Text>
                            </Button>
                            <Button
                                w="125px"
                                variant="dark"
                                fontSize="sm"
                                onClick={handleSubmit}
                                isLoading={isLoading}
                                isDisabled={isFormInvalid}
                                _hover={isFormInvalid ? {} : undefined}
                            >
                                <Text>
                                    {t("system.actions.save")}
                                </Text>
                            </Button>
                        </Flex>
                    </form>
                </CardBody>
            </Card>
        </Flex>
    );
};

export default ChangePasswordPage;